import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Grid,
  Link,
  TextField,
  MenuItem,
  Typography,
  IconButton,
  InputAdornment,
  Checkbox,
  FormControlLabel,
} from '@mui/material'
import LoadingButton from '@mui/lab/LoadingButton'
import { FC, useState, useReducer, useEffect, useRef } from 'react'
import { useAppDispatch, useAppSelector } from '../../../../hooks'
import { showAlertAction } from '../../../../store/alerts'
import {
  getUserProfileDataAction,
  updateUserProfileActionB,
  updateUserProfileResetAction,
} from '../../../../store/users'
import SelectStateItem, {
  StateOption,
  getState,
} from '../../Customers/SelectStateItem'
import CustomNumberField from './CustomNumberField'
import GSTINDetailsItem from '../../../../../src/components/GSTINDetailsItem'
import {
  EWayCredentials,
  EInvoiceCredentials,
  EmailCredentials,
  uploadObject,
  createSendLinkOTP,
  createVerifyLinkOtp,
  SendOTPDataObject,
} from '../../../../services'
import VerifyOTP from '../login/VerifyOTP'
import { getOrCreateDeviceId, getDeviceName } from '../../../../utils'
import { resizeImage } from '../../../../utils/imageUtils'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import { useLocation, useNavigate } from 'react-router'
import PhotoCamera from '@mui/icons-material/PhotoCamera'

type StoreProfileFields =
  | 'name'
  | 'mobile'
  | 'email'
  | 'address'
  | 'address2'
  | 'businessAddress'
  | 'billingAddress1'
  | 'billingAddress2'
  | 'billingPlace'
  | 'billingPincode'
  | 'storeName'
  | 'state'
  | 'gstin'
  | 'hsnCode'
  | 'ewayHsnCode'
  | 'place'
  | 'pincode'
  | 'nonGSTName'
  | 'nonGSTAddress'
  | 'eWayUserName'
  | 'eWayPassword'
  | 'eInvoiceUserName'
  | 'eInvoicePassword'
  | 'emailCredentialsEmail'
  | 'emailCredentialsPassword'

type StoreProfileError = Record<StoreProfileFields, Record<string, string>>
const storeProfileErros: StoreProfileError = {
  name: {
    empty: '',
  },
  mobile: {
    empty: '',
  },
  email: {
    empty: '',
  },
  storeName: {
    empty: '',
  },
  address: {
    empty: 'Godown Address is required',
  },
  address2: {
    empty: '',
  },
  businessAddress: {
    empty: '',
  },
  billingAddress1: {
    empty: 'Office Address Line 1 is required',
  },
  billingAddress2: {
    empty: '',
  },
  billingPlace: {
    empty: '',
  },
  billingPincode: {
    empty: 'Pincode must be exactly 6 digits',
  },
  state: {
    empty: '',
  },
  gstin: {
    empty: '',
  },
  hsnCode: {
    empty: '',
  },
  ewayHsnCode: {
    empty: '',
  },
  place: {
    empty: '',
  },
  pincode: {
    empty: 'Pincode must be exactly 6 digits',
  },
  nonGSTName: {
    empty: '',
  },
  nonGSTAddress: {
    empty: '',
  },
  eWayUserName: {
    empty: '',
  },
  eWayPassword: {
    empty: '',
  },
  eInvoiceUserName: {
    empty: '',
  },
  eInvoicePassword: {
    empty: '',
  },
  emailCredentialsEmail: {
    empty: '',
  },
  emailCredentialsPassword: {
    empty: '',
  },
}

type InputBox = {
  value: string
  error: boolean
  helperText: string
}

type StoreProfileFormState = Record<StoreProfileFields, InputBox>

const defaultObj: InputBox = {
  value: '',
  error: false,
  helperText: '',
}

const initialState: StoreProfileFormState = {
  name: { ...defaultObj },
  mobile: { ...defaultObj },
  email: { ...defaultObj },
  address: { ...defaultObj },
  address2: { ...defaultObj },
  businessAddress: { ...defaultObj },
  billingAddress1: { ...defaultObj },
  billingAddress2: { ...defaultObj },
  billingPlace: { ...defaultObj },
  billingPincode: { ...defaultObj },
  storeName: { ...defaultObj },
  state: { ...defaultObj },
  gstin: { ...defaultObj },
  hsnCode: { ...defaultObj },
  ewayHsnCode: { ...defaultObj },
  place: { ...defaultObj },
  pincode: { ...defaultObj },
  nonGSTName: { ...defaultObj },
  nonGSTAddress: { ...defaultObj },
  eWayUserName: { ...defaultObj },
  eWayPassword: { ...defaultObj },
  eInvoiceUserName: { ...defaultObj },
  eInvoicePassword: { ...defaultObj },
  emailCredentialsEmail: { ...defaultObj },
  emailCredentialsPassword: { ...defaultObj },
}

type Action = {
  type: 'SET' | 'ERROR'
  payload: {
    name: StoreProfileFields
    value: string
  }
}
const storeProfileReducer = (state, action) => {
  const {
    type,
    payload: { name, value },
  } = action
  switch (type) {
    case 'SET':
      return {
        ...state,
        [name]: { ...state[name], error: false, value: value, helperText: '' },
      }
    case 'ERROR':
      return {
        ...state,
        [name]: {
          ...state[name],
          error: true,
          value: value,
          helperText: storeProfileErros[name]['empty'],
        },
      }
    default:
      return state
  }
}

export interface StoreProfileProps {
  children?: React.ReactNode
  isOnboarding?: boolean
  nextClicked: boolean
}

const StoreProfilePage: FC<StoreProfileProps> = ({
  children,
  isOnboarding,
  nextClicked,
}) => {
  const { success, data } = useAppSelector((state) => state.userData)
  const { success: updateSuccess, message: updateErrorMessage } =
    useAppSelector((state) => state.updateProfile)

  const dispatchAction = useAppDispatch()
  const [profileFormData, dispatch] = useReducer(
    storeProfileReducer,
    initialState
  )
  const [stateOption, setStateOption] = useState<StateOption | null>()
  const [billingStateOption, setBillingStateOption] = useState<StateOption | null>(null)
  const [showPassword, setShowPassword] = useState(false)
  const [showEInvoicePassword, setShowEInvoicePassword] = useState(false)
  const [showEmailCredentialsPassword, setShowEmailCredentialsPassword] =
    useState(false)
  const [businessLogo, setBusinessLogo] = useState<string | null>(null)
  const [isUploadingLogo, setIsUploadingLogo] = useState(false)
  const [signatureUrl, setSignatureUrl] = useState<string | null>(null)
  const [isUploadingSignature, setIsUploadingSignature] = useState(false)
  const [signingEnabled, setSigningEnabled] = useState<boolean>(false)
  const [applyOnRentalEntries, setApplyOnRentalEntries] =
    useState<boolean>(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const signatureFileInputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()
  const location2 = useLocation()

  const {
    name,
    mobile,
    email,
    address,
    address2,
    businessAddress,
    billingAddress1,
    billingAddress2,
    billingPlace,
    billingPincode,
    storeName,
    gstin,
    hsnCode,
    ewayHsnCode,
    place,
    pincode,
    nonGSTName,
    nonGSTAddress,
    eWayUserName,
    eWayPassword,
    eInvoiceUserName,
    eInvoicePassword,
    emailCredentialsEmail,
    emailCredentialsPassword,
  } = profileFormData

  const [otpDialogOpen, setOtpDialogOpen] = useState(false)
  const [linkingField, setLinkingField] = useState<'mobile' | 'email' | null>(
    null
  )
  const [linkOtpSentResponse, setLinkOtpSentResponse] =
    useState<SendOTPDataObject | null>(null)
  const [linkOtpLoading, setLinkOtpLoading] = useState(false)
  const [linkVerifyLoading, setLinkVerifyLoading] = useState(false)
  const [linkIdentifier, setLinkIdentifier] = useState('')
  const [linkOtpError, setLinkOtpError] = useState<string | null>(null)
  const [mobileVerified, setMobileVerified] = useState(false)
  const [emailVerified, setEmailVerified] = useState(false)

  const onChangeHandler = (e) => {
    const {
      target: { name, value },
    } = e
    if (
      name == 'eWayUserName' ||
      name == 'eWayPassword' ||
      name == 'eInvoiceUserName' ||
      name == 'eInvoicePassword' ||
      name == 'emailCredentialsEmail' ||
      name == 'emailCredentialsPassword' ||
      name == 'email' ||
      name == 'mobile' ||
      name == 'address2' ||
      name == 'billingAddress2' ||
      name == 'billingPlace' ||
      name == 'billingPincode' ||
      name == 'place' ||
      name == 'pincode'
    ) {
      if (name === 'pincode' || name === 'billingPincode') {
        const val = value.replace(/\D/g, '').slice(0, 6)
        dispatch({ type: 'SET', payload: { name, value: val } })
        return
      }
      dispatch({ type: 'SET', payload: { name, value } })
      return
    }
    if (value.length > 0) {
      dispatch({ type: 'SET', payload: { name, value } })
    } else {
      dispatch({ type: 'ERROR', payload: { name, value } })
    }
  }

  // Fetch fresh profile data when component mounts
  useEffect(() => {
    dispatchAction(getUserProfileDataAction())
  }, [dispatchAction])

  useEffect(() => {
    if (nextClicked) {
      submitHandle()
    }
  }, [nextClicked])

  const validateEWayCredentials = (eWayUserName, eWayPassword) => {
    const errors = {}
    if (!eWayUserName || !eWayUserName.trim()) {
      errors['eWayUserName'] = eWayUserName
    }
    if (!eWayPassword || !eWayPassword.trim()) {
      errors['eWayPassword'] = eWayPassword
    }
    return errors
  }

  const buildEWayCredentials = (eWayUserName, eWayPassword) => {
    return {
      id: data.store.eWayCredentials?.id,
      userName: eWayUserName,
      password: eWayPassword,
    }
  }

  const validateAndBuildEWayCredentials = () => {
    const errors = validateEWayCredentials(
      eWayUserName?.value,
      eWayPassword?.value
    )
    if (Object.keys(errors).length === 0) {
      return buildEWayCredentials(eWayUserName.value, eWayPassword.value)
    } else {
      Object.entries(errors).forEach(([name, value]) => {
        dispatch({ type: 'ERROR', payload: { name, value } })
      })
      return null
    }
  }

  const buildEInvoiceCredentials = (userName, password) => {
    return {
      id: data.store.eInvoiceCredentials?.id,
      userName,
      password,
    }
  }

  const validateAndBuildEInvoiceCredentials = () => {
    const errors = validateEWayCredentials(
      eInvoiceUserName?.value,
      eInvoicePassword?.value
    )
    if (Object.keys(errors).length === 0) {
      return buildEInvoiceCredentials(
        eInvoiceUserName.value,
        eInvoicePassword.value
      )
    } else {
      Object.entries(errors).forEach(([name, value]) => {
        dispatch({ type: 'ERROR', payload: { name, value } })
      })
      return null
    }
  }

  const buildEmailCredentials = (emailValue, password) => {
    return {
      id: data.store.emailCredentials?.id,
      email: emailValue,
      password,
    }
  }

  const validateEmailCredentials = (emailValue, password) => {
    const errors = {}
    if (!emailValue || !emailValue.trim()) {
      errors['emailCredentialsEmail'] = emailValue
    }
    if (!password || !password.trim()) {
      errors['emailCredentialsPassword'] = password
    }
    return errors
  }

  const validateAndBuildEmailCredentials = () => {
    const errors = validateEmailCredentials(
      emailCredentialsEmail?.value,
      emailCredentialsPassword?.value
    )
    if (Object.keys(errors).length === 0) {
      return buildEmailCredentials(
        emailCredentialsEmail.value,
        emailCredentialsPassword.value
      )
    }
    Object.entries(errors).forEach(([name, value]) => {
      dispatch({ type: 'ERROR', payload: { name, value } })
    })
    return null
  }

  const submitHandle = (
    logoUrl?: string,
    newSignatureUrl?: string,
    signs?: boolean
  ) => {
    const eWayCredentialsProvided = eWayUserName?.value || eWayPassword?.value
    let eWayCredentials: EWayCredentials | undefined
    if (eWayCredentialsProvided) {
      eWayCredentials = validateAndBuildEWayCredentials()
      if (!eWayCredentials) {
        return
      }
    }
    const eInvoiceCredentialsProvided =
      eInvoiceUserName?.value || eInvoicePassword?.value
    let eInvoiceCredentials: EInvoiceCredentials | undefined
    if (eInvoiceCredentialsProvided) {
      eInvoiceCredentials = validateAndBuildEInvoiceCredentials()
      if (!eInvoiceCredentials) {
        return
      }
    }
    const emailCredentialsProvided =
      emailCredentialsEmail?.value || emailCredentialsPassword?.value
    let emailCredentials: EmailCredentials | undefined
    if (emailCredentialsProvided) {
      emailCredentials = validateAndBuildEmailCredentials()
      if (!emailCredentials) {
        return
      }
    }

    let hasErrors = false

    // Validate pincodes
    const pinVal = (pincode?.value ?? '').trim()
    if (pinVal && pinVal.length !== 6) {
      dispatch({ type: 'ERROR', payload: { name: 'pincode', value: pincode.value } })
      hasErrors = true
    }

    const billingPinVal = (billingPincode?.value ?? '').trim()
    if (billingPinVal && billingPinVal.length !== 6) {
      dispatch({ type: 'ERROR', payload: { name: 'billingPincode', value: billingPincode.value } })
      hasErrors = true
    }

    // Validate required fields
    if (!storeName.value?.length) {
      dispatch({ type: 'ERROR', payload: { name: 'storeName' } })
      hasErrors = true
    }

    const secondaryGodownFilled =
      !!address2.value?.trim() ||
      !!place.value?.trim() ||
      !!(pincode?.value?.trim()) ||
      !!stateOption

    if (secondaryGodownFilled && !address.value?.trim()) {
      dispatch({ type: 'ERROR', payload: { name: 'address' } })
      hasErrors = true
    }

    if (!isOnboarding && !billingAddress1.value?.length) {
      dispatch({ type: 'ERROR', payload: { name: 'billingAddress1' } })
      hasErrors = true
    }

    if (hasErrors) {
      return
    }

    const request: any = {
      name: storeName.value,
      ...(!isOnboarding
        ? {
            mobile: mobile.value || undefined,
            email: email.value || undefined,
          }
        : {}),
      store: {
        name: storeName.value,
        address: address.value,
        address2: address2.value || null,
        businessAddress: data.store.businessAddress || null,
        billingAddress: {
          add1: billingAddress1.value,
          add2: billingAddress2.value || null,
          place: billingPlace.value || null,
          pincode: billingPincode.value ? Number(billingPincode.value) : null,
          state: billingStateOption?.label || null,
          stateCode: billingStateOption?.code || null,
        },
        nonGSTName: nonGSTName.value,
        nonGSTAddress: nonGSTAddress.value,
        gstin: gstin?.value,
        hsnCode: isOnboarding ? '995457' : hsnCode?.value,
        ewayHsnCode: isOnboarding ? '730800' : ewayHsnCode?.value,
        state: stateOption?.label,
        stateCode: stateOption?.code,
        place: place?.value,
        pincode: pincode?.value ? Number(pincode?.value) : null,
        businessLogo: logoUrl !== undefined ? logoUrl : businessLogo,
        signUrl:
          newSignatureUrl !== undefined ? newSignatureUrl : signatureUrl,
        signingEnabled: signs ?? signingEnabled,
      },
      updateSac: applyOnRentalEntries,
    }

    // Only include eWayCredentials if it's defined
    if (eWayCredentials) {
      request.store.eWayCredentials = eWayCredentials
    }
    if (eInvoiceCredentials) {
      request.store.eInvoiceCredentials = eInvoiceCredentials
    }
    if (emailCredentials) {
      request.store.emailCredentials = emailCredentials
    }

    dispatchAction(updateUserProfileActionB(request))
  }

  useEffect(() => {
    if (success) {
      dispatch({ type: 'SET', payload: { name: 'name', value: data.name } })
      dispatch({
        type: 'SET',
        payload: {
          name: 'businessAddress',
          value: data.store.businessAddress ?? '',
        },
      })
      dispatch({
        type: 'SET',
        payload: { name: 'address', value: data.store.address },
      })
      dispatch({
        type: 'SET',
        payload: { name: 'address2', value: data.store.address2 ?? '' },
      })
      const details = data.store.billingAddress
      dispatch({
        type: 'SET',
        payload: { name: 'billingAddress1', value: details?.add1 ?? '' },
      })
      dispatch({
        type: 'SET',
        payload: { name: 'billingAddress2', value: details?.add2 ?? '' },
      })
      dispatch({
        type: 'SET',
        payload: { name: 'billingPlace', value: details?.place ?? '' },
      })
      dispatch({
        type: 'SET',
        payload: { name: 'billingPincode', value: details?.pincode ? String(details.pincode) : '' },
      })
      if (details?.state && details?.stateCode) {
        setBillingStateOption({ code: details.stateCode, label: details.state })
      } else {
        setBillingStateOption(null)
      }
      dispatch({ type: 'SET', payload: { name: 'mobile', value: data.mobile } })
      dispatch({
        type: 'SET',
        payload: { name: 'email', value: data.email ?? '' },
      })
      dispatch({
        type: 'SET',
        payload: { name: 'storeName', value: data.store.name },
      })
      dispatch({
        type: 'SET',
        payload: { name: 'state', value: data.store.state },
      })
      dispatch({
        type: 'SET',
        payload: { name: 'gstin', value: data.store.gstin },
      })
      dispatch({
        type: 'SET',
        payload: { name: 'hsnCode', value: data.store.hsnCode },
      })
      dispatch({
        type: 'SET',
        payload: { name: 'ewayHsnCode', value: data.store.ewayHsnCode },
      })
      dispatch({
        type: 'SET',
        payload: { name: 'place', value: data.store.place },
      })
      dispatch({
        type: 'SET',
        payload: { name: 'pincode', value: data.store.pincode != null ? String(data.store.pincode) : '' },
      })
      dispatch({
        type: 'SET',
        payload: { name: 'nonGSTName', value: data.store.nonGSTName },
      })
      dispatch({
        type: 'SET',
        payload: { name: 'nonGSTAddress', value: data.store.nonGSTAddress },
      })
      if (data.store?.state && data.store?.stateCode) {
        setStateOption({ code: data.store.stateCode, label: data.store.state })
      }
      if (data.store?.eWayCredentials) {
        const eWayCredentials = data.store?.eWayCredentials
        dispatch({
          type: 'SET',
          payload: {
            name: 'eWayUserName',
            value: eWayCredentials.userName,
          },
        })
        dispatch({
          type: 'SET',
          payload: {
            name: 'eWayPassword',
            value: eWayCredentials.password,
          },
        })
      }
      if (data.store?.eInvoiceCredentials) {
        const eInvoiceCredentials = data.store?.eInvoiceCredentials
        dispatch({
          type: 'SET',
          payload: {
            name: 'eInvoiceUserName',
            value: eInvoiceCredentials.userName,
          },
        })
        dispatch({
          type: 'SET',
          payload: {
            name: 'eInvoicePassword',
            value: eInvoiceCredentials.password,
          },
        })
      }
      if (data.store?.emailCredentials) {
        const emailCredentialsData = data.store.emailCredentials
        dispatch({
          type: 'SET',
          payload: {
            name: 'emailCredentialsEmail',
            value: emailCredentialsData.email,
          },
        })
        dispatch({
          type: 'SET',
          payload: {
            name: 'emailCredentialsPassword',
            value: emailCredentialsData.password,
          },
        })
      }
      // Set business logo if exists
      if (data.store?.businessLogo) {
        setBusinessLogo(data.store.businessLogo)
      }
      // Set signature if exists
      if (data.store?.signUrl) {
        setSignatureUrl(data.store.signUrl)
      }
      // Set signing enabled if exists
      if (data.store?.signingEnabled !== undefined) {
        setSigningEnabled(data.store.signingEnabled)
      }
    }
  }, [success])

  useEffect(() => {
    if (updateSuccess) {
      dispatchAction(getUserProfileDataAction())
      dispatchAction(
        showAlertAction({
          message: 'Profile Updated Successfully',
          severity: 'success',
        })
      )

      // Check if we came from Add Site flow (not Edit Site)
      if (
        location2.state?.fromAddSite === true &&
        !location2.state?.isEditMode
      ) {
        // Navigate back to current accounts page with reopenAddSite state (only for Add Site)
        navigate('/dashboard/current-account', {
          state: { reopenAddSite: true },
        })
      } else if (location2.state?.from) {
        navigate(location2.state.from)
      }
      // For Edit Site case, stay on profile page (normal flow)

      dispatchAction(updateUserProfileResetAction())
    } else if (updateErrorMessage) {
      dispatchAction(
        showAlertAction({ message: updateErrorMessage, severity: 'error' })
      )
      dispatchAction(updateUserProfileResetAction())
    }
  }, [updateSuccess, updateErrorMessage, location2.state, navigate])

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword)
  }

  const handleLogoUpload = async (file: File) => {
    try {
      setIsUploadingLogo(true)

      // Resize image to 200x200
      const resizedFile = await resizeImage(file, 200, 200)

      // Upload to S3
      const uploadResult = await uploadObject(resizedFile.name, resizedFile)

      // Set the logo URL
      setBusinessLogo(uploadResult.downloadUrl || null)
      if (uploadResult && uploadResult.downloadUrl) {
        submitHandle(uploadResult.downloadUrl)
      }

      dispatchAction(
        showAlertAction({
          message: 'Business logo uploaded successfully',
          severity: 'success',
        })
      )
    } catch (error) {
      console.error('Error uploading logo:', error)
      dispatchAction(
        showAlertAction({
          message: `Failed to upload business logo: ${error.message}`,
          severity: 'error',
        })
      )
    } finally {
      setIsUploadingLogo(false)
    }
  }

  const handleSignatureUpload = async (file: File) => {
    try {
      setIsUploadingSignature(true)

      // Resize image to 200x200
      const resizedFile = await resizeImage(file, 200, 200)

      // Upload to S3
      const uploadResult = await uploadObject(resizedFile.name, resizedFile)

      // Set the signature URL
      setSignatureUrl(uploadResult.downloadUrl || null)
      if (uploadResult && uploadResult.downloadUrl) {
        submitHandle(undefined, uploadResult.downloadUrl, true)
      }

      dispatchAction(
        showAlertAction({
          message: 'Signature uploaded successfully',
          severity: 'success',
        })
      )
    } catch (error) {
      console.error('Error uploading signature:', error)
      dispatchAction(
        showAlertAction({
          message: `Failed to upload signature: ${error.message}`,
          severity: 'error',
        })
      )
    } finally {
      setIsUploadingSignature(false)
    }
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      if (file.type.startsWith('image/')) {
        handleLogoUpload(file)
      } else {
        dispatchAction(
          showAlertAction({
            message: 'Please select a valid image file (JPG or PNG)',
            severity: 'error',
          })
        )
      }
    }
  }

  const handleSignatureFileSelect = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0]
    if (file) {
      if (file.type.startsWith('image/')) {
        handleSignatureUpload(file)
      } else {
        dispatchAction(
          showAlertAction({
            message: 'Please select a valid image file (JPG or PNG)',
            severity: 'error',
          })
        )
      }
    }
  }

  const handleGetOTP = async (field: 'mobile' | 'email') => {
    const value = field === 'mobile' ? mobile.value : email.value

    if (field === 'mobile') {
      const mobileRegex = /[6789]\d{9}$/
      if (!value || !mobileRegex.test(value)) {
        dispatch({
          type: 'ERROR',
          payload: { name: 'mobile', value: value || '' },
        })
        return
      }
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!value || !emailRegex.test(value)) {
        dispatch({
          type: 'ERROR',
          payload: { name: 'email', value: value || '' },
        })
        return
      }
    }

    const identifier =
      field === 'mobile'
        ? value.startsWith('+')
          ? value
          : `+91${value}`
        : value
    setLinkIdentifier(identifier)
    setLinkingField(field)
    setLinkOtpLoading(true)
    setLinkOtpError(null)

    try {
      const response = await createSendLinkOTP(identifier)
      setLinkOtpLoading(false)
      if (response?.data?.data) {
        setLinkOtpSentResponse(response.data.data)
        setOtpDialogOpen(true)
      } else {
        const errorMessage =
          response?.data?.message || 'Failed to send OTP. Please try again.'
        dispatchAction(
          showAlertAction({ message: errorMessage, severity: 'error' })
        )
        dispatch({
          type: 'ERROR',
          payload: { name: field, value: value || '' },
        })
      }
    } catch {
      setLinkOtpLoading(false)
      dispatchAction(
        showAlertAction({
          message: 'Failed to send OTP. Please try again.',
          severity: 'error',
        })
      )
      dispatch({
        type: 'ERROR',
        payload: { name: field, value: value || '' },
      })
    }
  }

  const handleVerifyLinkOTP = async (otp: string) => {
    if (otp.length !== 6 || !linkOtpSentResponse) return

    setLinkVerifyLoading(true)
    setLinkOtpError(null)
    try {
      const response = await createVerifyLinkOtp({
        otp,
        verificationId: linkOtpSentResponse.verificationId,
        deviceId: getOrCreateDeviceId(),
        deviceName: getDeviceName(),
      })
      setLinkVerifyLoading(false)
      if (response?.data?.data) {
        setOtpDialogOpen(false)
        if (linkingField === 'mobile') setMobileVerified(true)
        else setEmailVerified(true)
      } else {
        setLinkOtpError(response.data?.message ?? 'Invalid OTP')
      }
    } catch {
      setLinkVerifyLoading(false)
      setLinkOtpError('Failed to verify OTP')
    }
  }

  const handleResendLinkOTP = async () => {
    try {
      const response = await createSendLinkOTP(linkIdentifier)
      if (response?.data?.data) {
        setLinkOtpSentResponse(response.data.data)
      }
    } catch {
      // silent
    }
  }

  const triggerFileInput = () => {
    fileInputRef.current?.click()
  }

  const triggerSignatureFileInput = () => {
    signatureFileInputRef.current?.click()
  }

  const handleRemoveLogo = () => {
    setBusinessLogo(null)
    submitHandle('') // Pass empty string to remove logo
  }

  const handleRemoveSignature = () => {
    setSignatureUrl(null)
    submitHandle(undefined, '', false) // Pass empty string to remove signature
  }

  const gridPadding = 1.4
  return (
    <Box
      component="form"
      pl={1.5}
      pr={1.5}
      pt={1}
      noValidate
      autoComplete="off"
    >
      {/* ✅ Page Header with Subscription End Date */}
      {!isOnboarding && data?.subscription?.endDate && (
        <Box
          sx={{
            px: 1,
            py: 0.8,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              color: 'white',
              fontWeight: 600,
              fontSize: '0.875rem',
              backgroundColor: 'linear-gradient(45deg, #ff6b6b, #4ecdc4)',
              background: 'linear-gradient(45deg, #ff6b6b, #4ecdc4)',
              px: 2,
              py: 0.5,
              borderRadius: 1,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            Subscription end:{' '}
            {new Date(data.subscription.endDate).toLocaleDateString('en-GB')}
          </Typography>
        </Box>
      )}

      {/* ✅ Business Logo & Signature Upload */}
      {!isOnboarding && (
        <Grid container sx={{ mb: 0 }}>
          {/* Business Logo */}
          <Grid item xs={6}>
            <Typography variant="h5" m={1}>
              Business Logo:
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: gridPadding,
                mb: 2,
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  border: '1px solid #ccc',
                  borderRadius: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 1,
                  backgroundColor: '#f5f5f5',
                }}
              >
                {businessLogo ? (
                  <img
                    src={businessLogo}
                    alt="Business Logo"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                    }}
                  />
                ) : (
                  <PhotoCamera />
                )}
              </Box>
              <Box>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <Button
                    variant="outlined"
                    component="label"
                    disabled={isUploadingLogo}
                    onClick={triggerFileInput}
                    startIcon={<PhotoCamera />}
                  >
                    {isUploadingLogo ? 'Uploading...' : 'Upload Logo'}
                  </Button>
                  {businessLogo && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleRemoveLogo}
                      disabled={isUploadingLogo}
                    >
                      Remove
                    </Button>
                  )}
                </Box>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png"
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                />
                <Typography
                  variant="caption"
                  display="block"
                  sx={{ mt: 1, color: 'text.secondary' }}
                >
                  Upload JPG or PNG image.
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Signature */}
          <Grid item xs={6}>
            <Typography variant="h5" m={1}>
              Signature:
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: gridPadding,
                mb: 2,
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  border: '1px solid #ccc',
                  borderRadius: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 1,
                  backgroundColor: '#f5f5f5',
                }}
              >
                {signatureUrl ? (
                  <img
                    src={signatureUrl}
                    alt="Signature"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                    }}
                  />
                ) : (
                  <PhotoCamera />
                )}
              </Box>
              <Box>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <Button
                    variant="outlined"
                    component="label"
                    disabled={isUploadingSignature}
                    onClick={triggerSignatureFileInput}
                    startIcon={<PhotoCamera />}
                  >
                    {isUploadingSignature ? 'Uploading...' : 'Upload Signature'}
                  </Button>
                  {signatureUrl && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleRemoveSignature}
                      disabled={isUploadingSignature}
                    >
                      Remove
                    </Button>
                  )}
                </Box>
                <input
                  ref={signatureFileInputRef}
                  type="file"
                  accept="image/jpeg,image/png"
                  style={{ display: 'none' }}
                  onChange={handleSignatureFileSelect}
                />
                <Typography
                  variant="caption"
                  display="block"
                  sx={{ mt: 1, color: 'text.secondary' }}
                >
                  Upload JPG or PNG image.
                </Typography>
                {/* Add signature on PDFs checkbox - moved below signature upload */}
              </Box>
            </Box>
            <Box sx={{ mt: 2, ml: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={signingEnabled}
                    onChange={(e) => setSigningEnabled(e.target.checked)}
                    color="primary"
                  />
                }
                label="Add signature on PDFs"
              />
            </Box>
          </Grid>
        </Grid>
      )}
      <Grid container>
        <Grid item xs={12}>
          <Typography variant="h5" m={1} mt={2}>
            Business Details:
          </Typography>
        </Grid>

        <Grid item xs={6} p={gridPadding}>
          <GSTINDetailsItem
            initialGstin={gstin.value ?? ''}
            initialgstinError={gstin.error}
            placeholder="GSTIN (Optional)"
            onUpdate={(gstin, gstinObj) => {
              dispatch({
                type: 'SET',
                payload: { name: 'gstin', value: gstin },
              })
              if (gstinObj && gstinObj.tradeName) {
                const stateOpt = getState(gstinObj.stateCode)
                dispatch({
                  type: 'SET',
                  payload: { name: 'pincode', value: gstinObj.pinCode || '' },
                })
                dispatch({
                  type: 'SET',
                  payload: { name: 'place', value: gstinObj.place || '' },
                })
                dispatch({
                  type: 'SET',
                  payload: { name: 'storeName', value: gstinObj.tradeName },
                })
                dispatch({
                  type: 'SET',
                  payload: { name: 'address', value: gstinObj.address1 || '' },
                })
                dispatch({
                  type: 'SET',
                  payload: { name: 'address2', value: gstinObj.address2 || '' },
                })
                dispatch({
                  type: 'SET',
                  payload: { name: 'billingAddress1', value: gstinObj.address1 || '' },
                })
                dispatch({
                  type: 'SET',
                  payload: { name: 'billingAddress2', value: gstinObj.address2 || '' },
                })
                dispatch({
                  type: 'SET',
                  payload: { name: 'billingPlace', value: gstinObj.place || '' },
                })
                dispatch({
                  type: 'SET',
                  payload: { name: 'billingPincode', value: gstinObj.pinCode || '' },
                })
                setStateOption(stateOpt)
                setBillingStateOption(stateOpt)
              }
            }}
          />
        </Grid>
        <Grid item xs={6} p={gridPadding}>
          <TextField
            size="small"
            name="storeName"
            value={storeName.value}
            label="Business Name"
            error={storeName.error}
            required
            helperText={storeName.helperText}
            onChange={onChangeHandler}
            fullWidth
          ></TextField>
        </Grid>

        <Grid item xs={6}>
          <Grid container spacing={0}>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ m: 1, mt: 2, fontWeight: 'bold' }}>
                Office / Billing Address
              </Typography>
            </Grid>

            <Grid item xs={6} p={gridPadding}>
              <TextField
                size="small"
                name="billingAddress1"
                value={billingAddress1.value}
                label="Address Line 1"
                error={billingAddress1.error}
                required
                helperText={billingAddress1.helperText}
                onChange={onChangeHandler}
                fullWidth
              />
            </Grid>

            <Grid item xs={6} p={gridPadding}>
              <TextField
                size="small"
                name="billingAddress2"
                value={billingAddress2.value}
                label="Address Line 2 (Optional)"
                error={billingAddress2.error}
                helperText={billingAddress2.helperText}
                onChange={onChangeHandler}
                fullWidth
              />
            </Grid>

            <Grid item xs={6} p={gridPadding}>
              <TextField
                size="small"
                name="billingPlace"
                value={billingPlace.value}
                label="Place (Optional)"
                error={billingPlace.error}
                helperText={billingPlace.helperText}
                onChange={onChangeHandler}
                fullWidth
              />
            </Grid>

            <Grid item xs={6} p={gridPadding}>
              <CustomNumberField
                size="small"
                maxLength={6}
                name="billingPincode"
                value={billingPincode.value}
                label="Pincode (Optional)"
                error={billingPincode.error}
                helperText={billingPincode.error ? billingPincode.helperText : ''}
                onChange={onChangeHandler}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} p={gridPadding}>
              <SelectStateItem
                label="State"
                selectedStateOption={billingStateOption}
                onChange={(option) => {
                  setBillingStateOption(option)
                }}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={6}>
          <Grid container spacing={0}>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ m: 1, mt: 2, fontWeight: 'bold' }}>
                Godown Address
              </Typography>
            </Grid>

            <Grid item xs={6} p={gridPadding}>
              <TextField
                size="small"
                name="address"
                value={address.value}
                label="Address Line 1"
                error={address.error}
                required
                helperText={address.helperText}
                onChange={onChangeHandler}
                fullWidth
              />
            </Grid>

            <Grid item xs={6} p={gridPadding}>
              <TextField
                size="small"
                name="address2"
                value={address2.value}
                label="Address Line 2 (Optional)"
                error={address2.error}
                helperText={address2.helperText}
                onChange={onChangeHandler}
                fullWidth
              />
            </Grid>

            <Grid item xs={6} p={gridPadding}>
              <TextField
                size="small"
                name="place"
                value={place.value}
                label="Place (Optional)"
                error={place.error}
                helperText={place.helperText}
                onChange={onChangeHandler}
                fullWidth
              />
            </Grid>

            <Grid item xs={6} p={gridPadding}>
              <CustomNumberField
                size="small"
                maxLength={6}
                name="pincode"
                value={pincode.value}
                label="Pincode (Optional)"
                error={pincode.error}
                helperText={pincode.error ? pincode.helperText : ''}
                onChange={onChangeHandler}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} p={gridPadding}>
              <SelectStateItem
                label="State"
                selectedStateOption={stateOption}
                onChange={(option) => {
                  setStateOption(option)
                }}
              />
            </Grid>
          </Grid>
        </Grid>

        {!isOnboarding && (
          <>
            {/* Profile View — Mobile row */}
             <Grid item xs={12}>
              <Typography variant="h6" sx={{ ml:1, mb: 0.5, mt: 1, fontWeight: 'bold' }}>
                Contact/HSN/SAC
              </Typography>
            </Grid>
            <Grid item xs={6} p={gridPadding}>
              <TextField
                size="small"
                disabled={!!data?.mobile || mobileVerified}
                name="mobile"
                value={mobile.value || ''}
                label="Mobile"
                placeholder="Link Mobile Number"
                error={mobile.error}
                helperText={mobile.helperText}
                onChange={onChangeHandler}
                fullWidth
                InputLabelProps={{ shrink: true }}
                InputProps={
                  !data?.mobile && !mobileVerified
                    ? {
                        sx: { pr: 0.5 },
                        endAdornment: (
                          <InputAdornment position="end">
                            <LoadingButton
                              loading={
                                linkOtpLoading && linkingField === 'mobile'
                              }
                              size="small"
                              variant="text"
                              onClick={() => handleGetOTP('mobile')}
                              sx={{
                                color: '#1e88e5',
                                fontWeight: 400,
                                fontSize: '0.85rem',
                                px: 0.5,
                                minWidth: 'auto',
                              }}
                            >
                              VERIFY
                            </LoadingButton>
                          </InputAdornment>
                        ),
                      }
                    : undefined
                }
              />
            </Grid>
            
            <Grid item xs={3} p={gridPadding}>
              <CustomNumberField
                name="hsnCode"
                value={hsnCode.value}
                label="SAC Code(Optional)"
                error={hsnCode.error}
                helperText={hsnCode.helperText}
                onChange={onChangeHandler}
                maxLength={6}
                fullWidth
              />
            </Grid>
            <Grid item xs={3} p={gridPadding}>
              <TextField
                size="small"
                select
                name="applyOnRentalEntries"
                value={applyOnRentalEntries ? 'yes' : 'no'}
                label="Apply SAC on Rental Entries"
                onChange={(e) =>
                  setApplyOnRentalEntries(e.target.value === 'yes')
                }
                fullWidth
              >
                <MenuItem value="no">No</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
              </TextField>
            </Grid>

            {/* Profile View — Email row */}
            <Grid item xs={6} p={gridPadding}>
              <TextField
                size="small"
                disabled={!!data?.email || emailVerified}
                name="email"
                value={email.value || ''}
                label="Email"
                placeholder="Link Email Address"
                type="email"
                error={email.error}
                helperText={email.helperText}
                onChange={onChangeHandler}
                fullWidth
                InputLabelProps={{ shrink: true }}
                InputProps={
                  !data?.email && !emailVerified
                    ? {
                        sx: { pr: 0.5 },
                        endAdornment: (
                          <InputAdornment position="end">
                            <LoadingButton
                              loading={
                                linkOtpLoading && linkingField === 'email'
                              }
                              size="small"
                              variant="text"
                              onClick={() => handleGetOTP('email')}
                              sx={{
                                color: '#1e88e5',
                                fontWeight: 400,
                                fontSize: '0.85rem',
                                px: 0.5,
                                minWidth: 'auto',
                              }}
                            >
                              VERIFY
                            </LoadingButton>
                          </InputAdornment>
                        ),
                      }
                    : undefined
                }
              />
            </Grid>
            <Grid
              item
              xs={6}
              p={gridPadding}
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <CustomNumberField
                name="ewayHsnCode"
                value={ewayHsnCode.value}
                label="HSN Code(for eway bills)"
                error={ewayHsnCode.error}
                helperText={ewayHsnCode.helperText}
                onChange={onChangeHandler}
                maxLength={8}
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h5" m={1} mt={2}>
                EWay Credentials for Creating Eway Bills(Optional){' '}
                <Link
                  href="https://scaffoldingapp.com/pb-doc-service/2026/permanent/EWAY+CREDENTIALS+(5).pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  sx={{
                    fontWeight: 400,
                    fontSize: '0.85rem',
                    color: '#1e88e5',
                  }}
                >
                  How to Create?
                </Link>
              </Typography>
            </Grid>
            <Grid item xs={6} p={gridPadding}>
              <TextField
                size="small"
                name="eWayUserName"
                value={eWayUserName.value}
                label="User Name"
                error={eWayUserName.error}
                helperText={eWayUserName.helperText}
                onChange={onChangeHandler}
                fullWidth
              ></TextField>
            </Grid>
            <Grid item xs={6} p={gridPadding}>
              <TextField
                size="small"
                name="eWayPassword"
                value={eWayPassword.value}
                label="Password"
                type={showPassword ? 'text' : 'password'}
                error={eWayPassword.error}
                helperText={eWayPassword.helperText}
                onChange={onChangeHandler}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              ></TextField>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h5" m={1} mt={2}>
                E-Invoice Credentials for Creating E-Invoices(Optional){' '}
                <Link
                  href="https://scaffoldingapp.com/pb-doc-service/2026/permanent/EWAY+CREDENTIALS+(5).pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  sx={{
                    fontWeight: 400,
                    fontSize: '0.85rem',
                    color: '#1e88e5',
                  }}
                >
                  How to Create?
                </Link>
              </Typography>
            </Grid>
            <Grid item xs={6} p={gridPadding}>
              <TextField
                size="small"
                name="eInvoiceUserName"
                value={eInvoiceUserName.value}
                label="User Name"
                error={eInvoiceUserName.error}
                helperText={eInvoiceUserName.helperText}
                onChange={onChangeHandler}
                fullWidth
              ></TextField>
            </Grid>
            <Grid item xs={6} p={gridPadding}>
              <TextField
                size="small"
                name="eInvoicePassword"
                value={eInvoicePassword.value}
                label="Password"
                type={showEInvoicePassword ? 'text' : 'password'}
                error={eInvoicePassword.error}
                helperText={eInvoicePassword.helperText}
                onChange={onChangeHandler}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle e-invoice password visibility"
                        onClick={() =>
                          setShowEInvoicePassword(!showEInvoicePassword)
                        }
                        edge="end"
                      >
                        {showEInvoicePassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              ></TextField>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h5" m={1} mt={2}>
                Gmail credentials for email notifications (Optional){' '}
                <Link
                  href="https://scaffoldingapp.com/pb-doc-service/demo-videos/Gmail_App_Password_First_3_Steps.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  sx={{
                    fontWeight: 400,
                    fontSize: '0.85rem',
                    color: '#1e88e5',
                  }}
                >
                  How to Create?
                </Link>
              </Typography>
            </Grid>
            <Grid item xs={6} p={gridPadding}>
              <TextField
                size="small"
                name="emailCredentialsEmail"
                value={emailCredentialsEmail.value}
                label="Email"
                error={emailCredentialsEmail.error}
                helperText={emailCredentialsEmail.helperText}
                onChange={onChangeHandler}
                fullWidth
              ></TextField>
            </Grid>
            <Grid item xs={6} p={gridPadding}>
              <TextField
                size="small"
                name="emailCredentialsPassword"
                value={emailCredentialsPassword.value}
                label="App Password"
                type={showEmailCredentialsPassword ? 'text' : 'password'}
                error={emailCredentialsPassword.error}
                helperText={emailCredentialsPassword.helperText}
                onChange={onChangeHandler}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle email credentials password visibility"
                        onClick={() =>
                          setShowEmailCredentialsPassword(
                            !showEmailCredentialsPassword
                          )
                        }
                        edge="end"
                      >
                        {showEmailCredentialsPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              ></TextField>
            </Grid>
          </>
        )}
        <Grid item xs={12} mt={4} textAlign={'right'} pr={1.8} mb={1}>
          {!isOnboarding && (
            <Button
              variant="contained"
              sx={{ backgroundColor: 'green' }}
              onClick={() => submitHandle()}
            >
              {' '}
              SAVE PROFILE
            </Button>
          )}
        </Grid>
      </Grid>

      {/* OTP Dialog for linking mobile/email */}
      <Dialog
        open={otpDialogOpen}
        onClose={() => setOtpDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { minWidth: 500 } }}
      >
        <DialogContent sx={{ pt: 7, pb: 7, pl: 5, pr: 5 }}>
          {linkOtpSentResponse && (
            <VerifyOTP
              fieldWidth={300}
              otpHandler={handleVerifyLinkOTP}
              resendOTPHandler={handleResendLinkOTP}
              verifyOtpLoading={linkVerifyLoading}
              apiError={linkOtpError}
              expirationInSeconds={linkOtpSentResponse.expirationInSeconds}
              loginMethod={linkingField ?? 'mobile'}
              identifier={linkIdentifier}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  )
}
export default StoreProfilePage
